import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Bot, RefreshCw, Loader2, AlertTriangle, Power } from 'lucide-react';
import { toast } from 'sonner';
import {
  quantAutoTraderStatus, quantAutoTraderToggle, quantAutoTraderDecisions, getErrorMessage,
} from '../../lib/api';

/**
 * AutoTraderPanel — Kill switch + KPI + decisioni recenti. Polling 60s.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────┐
 *   │ 🤖 Auto-trader        [Toggle: ON/OFF]                  │
 *   ├─────────────────────────────────────────────────────────┤
 *   │ Decisions today: 3/20  · Open positions: 2/5            │
 *   │ Last run: 14:32:15  · Last error: —                     │
 *   ├─────────────────────────────────────────────────────────┤
 *   │ Ultime decisioni (10):                                  │
 *   │ 14:32 · donchian_breakout_20/SPY · skip · signal_flat   │
 *   └─────────────────────────────────────────────────────────┘
 */

const fmtTime = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const AutoTraderPanel = () => {
  const [status, setStatus] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sRes, dRes] = await Promise.all([
        quantAutoTraderStatus(),
        quantAutoTraderDecisions({ limit: 10 }),
      ]);
      setStatus(sRes.data);
      const rawDec = dRes.data?.items ?? dRes.data;
      setDecisions(Array.isArray(rawDec) ? rawDec : []);
    } catch (e) {
      setError(getErrorMessage(e, 'Errore auto-trader'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    timerRef.current = setInterval(fetchAll, 60000);
    return () => clearInterval(timerRef.current);
  }, [fetchAll]);

  const handleToggle = async () => {
    if (!status) return;
    const next = !status.enabled;
    setToggling(true);
    try {
      const r = await quantAutoTraderToggle(next);
      setStatus((prev) => ({ ...prev, enabled: r.data.enabled }));
      toast.success(`Auto-trader ${r.data.enabled ? 'attivato' : 'disattivato'}`, {
        description: `Aggiornato alle ${new Date(r.data.updated_at).toLocaleTimeString('it-IT')}`,
      });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore toggle auto-trader'));
    } finally {
      setToggling(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Bot className={`w-4 h-4 ${status?.enabled ? 'text-emerald-500' : 'text-muted-foreground'}`} />
            Auto-trader
            {loading && !status && (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
            )}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={fetchAll}
              disabled={loading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant={status?.enabled ? 'destructive' : 'default'}
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={handleToggle}
              disabled={toggling || !status}
              aria-label={status?.enabled ? 'Disattiva auto-trader' : 'Attiva auto-trader'}
            >
              {toggling ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : (
                <Power className="w-3 h-3 mr-1" />
              )}
              {status?.enabled ? 'ON — Disattiva' : 'OFF — Attiva'}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-500 bg-red-50 dark:bg-red-950/20 rounded px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            {error}
          </div>
        )}

        {/* KPI row */}
        {status && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg bg-muted/30 px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Decisioni oggi</p>
              <p className="text-lg font-bold tabular-nums">
                {status.decisions_today ?? '—'}
                <span className="text-sm font-normal text-muted-foreground">
                  /{status.max_decisions_per_day ?? '—'}
                </span>
              </p>
            </div>
            <div className="rounded-lg bg-muted/30 px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Posizioni aperte</p>
              <p className="text-lg font-bold tabular-nums">
                {status.open_positions ?? '—'}
                <span className="text-sm font-normal text-muted-foreground">
                  /{status.max_concurrent ?? '—'}
                </span>
              </p>
            </div>
            <div className="rounded-lg bg-muted/30 px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Ultimo run</p>
              <p className="text-base font-mono font-semibold tabular-nums">
                {fmtTime(status.last_run_at)}
              </p>
            </div>
            <div className={`rounded-lg px-3 py-2 ${status.last_error ? 'bg-red-50 dark:bg-red-950/20' : 'bg-muted/30'}`}>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Ultimo errore</p>
              {status.last_error ? (
                <p className="text-xs font-mono text-red-600 dark:text-red-400 truncate" title={status.last_error}>
                  {status.last_error}
                </p>
              ) : (
                <p className="text-base font-bold text-muted-foreground">—</p>
              )}
            </div>
          </div>
        )}

        {/* Decisioni recenti */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Ultime decisioni
          </p>
          {decisions.length === 0 ? (
            <p className="text-xs text-muted-foreground italic py-3">
              Nessuna decisione registrata.
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs w-20">Ora</TableHead>
                    <TableHead className="text-xs">Strategia / Asset</TableHead>
                    <TableHead className="text-xs">Azione</TableHead>
                    <TableHead className="text-xs">Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {decisions.map((d, idx) => (
                    <TableRow key={d.id || idx}>
                      <TableCell className="text-xs font-mono whitespace-nowrap">
                        {fmtTime(d.timestamp || d.created_at)}
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {[d.strategy, d.asset || d.symbol].filter(Boolean).join('/') || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            d.action === 'buy'
                              ? 'border-emerald-500 text-emerald-700 dark:text-emerald-400'
                              : d.action === 'sell'
                              ? 'border-red-500 text-red-700 dark:text-red-400'
                              : 'border-muted-foreground text-muted-foreground'
                          }`}
                        >
                          {d.action || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                        {d.reason || d.skip_reason || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AutoTraderPanel;
